import * as _ from 'lodash';

import type { ProjectGroupReleaseProject, Revision } from '../../../types';
import type { ProjectEntry } from '../../../redux/projects/store';

export type SortOptions = {
  key: string;
  asc: boolean;
};

export type GetValuer<T> = (target: T, key: string) => any;

/**
 * createProjectComparator will return a function suitable for comparing projects with the provided sort options.
 * @param sort The sort options for the projects that will be compared.
 * Options are optional: default key is `title` and default order is `asc`.
 */
export function createComparator<T>(sort: SortOptions, getValue?: GetValuer<T>): (a: T, b: T) => number {
  const { key = 'title', asc = true } = sort || {};

  return (a: T, b: T): number => {
    let aValue = getValue ? getValue(a, key) : _.get(a, key);
    let bValue = getValue ? getValue(b, key) : _.get(a, key);

    if (!asc) {
      [aValue, bValue] = [bValue, aValue];
    }

    if (_.isString(aValue) && _.isString(bValue)) {
      return aValue.localeCompare(bValue);
    }

    if (aValue > bValue) {
      return -1;
    }
    if (aValue < bValue) {
      return 1;
    }
    return 0;
  };
}

/**
 * Find the latest revision for a given release project. It utilizes
 * the current project entries (sources from redux) to determine if any revisions
 * have been loaded for the currently-selected branch.
 */
export function getLatestRevision(
  allProjects: Record<string, ProjectEntry>,
  releaseProject: ProjectGroupReleaseProject,
): Revision | undefined {
  // once a branch's revisions have been loaded, they form a mapping of branch->revisions
  const project = allProjects[releaseProject.projectId];
  const loadedBranches = (project?.revisions ?? {}) as Record<string, Revision[]>;
  const loadedRevisions = loadedBranches[releaseProject.branch];

  // if revisions have been loaded for this project, use them
  if (Array.isArray(loadedRevisions)) {
    // if this branch's revisions have been loaded but none exist, return undefined
    if (loadedRevisions.length === 0) {
      return undefined;
    }

    // otherwise, choose the first (latest) one
    return loadedRevisions[0];
  }

  // at this point, we haven't loaded any lists of revisions yet, so choose
  // from the data available from the project's latest revision

  // first, check the existing project's latest revision (when creating / updating releases)
  const latestRevisionFromExistingProject = releaseProject.latestRevision;
  // then, check the new project's latest revision (when creating new groups)
  const latestRevisionFromNewProject = releaseProject.project.revisions?.[0];

  return latestRevisionFromExistingProject ?? latestRevisionFromNewProject;
}
